import { AUDIENCE_MODE } from 'lib';

/**
 * The Campaign Builder's required fields, and which of them are still empty.
 *
 * One list, read by both the builder (to mark and explain its fields) and the scheduler (to say
 * why it will not send). They used to reason separately — the builder silently greyed out Save
 * Draft with no name, and the scheduler gave one reason at a time in a tooltip — so an operator
 * found out what was missing one field, and one hover, at a time.
 *
 * **Two thresholds, because the server has two.** A draft needs only its name: `createCampaign`
 * refuses nothing else, and a draft is meant to be unfinished. Scheduling or sending needs the rest
 * — a button destination, the FanCam challenge it points at, and an audience — because Phase 1 has
 * no default challenge, so a campaign without one has a button that goes nowhere.
 *
 * Conversion Action is deliberately absent. Empty is a real answer there: the campaign then
 * reports CTA Conversion as not measured instead of as 0%.
 */

/** Stable keys, used for field ids and for the scheduler's links back to each field. */
export const REQUIRED_FIELD = {
  NAME: 'name',
  DESTINATION: 'destination',
  CHALLENGE: 'challenge',
  AUDIENCE: 'audience'
};

/** The element to focus for each field, in the builder. */
export const requiredFieldId = (key) => `campaign-required-${key}`;

/**
 * Every required field that is not yet complete, in the order the builder shows them.
 *
 * Each entry is `{ key, label, message }`: `label` is the field's own name as the builder prints
 * it, so the scheduler's list and the field agree; `message` says what to do, for the field itself.
 */
export function missingCampaignFields({
  campaignName,
  destination,
  destinationTarget,
  challengeResolved,
  audienceMode,
  filtersWereSet,
  channelId,
  recipientId
}) {
  const missing = [];

  if (!campaignName?.trim()) {
    missing.push({
      key: REQUIRED_FIELD.NAME,
      label: 'Campaign Name',
      message: 'Enter a campaign name.'
    });
  }

  if (!destination) {
    missing.push({
      key: REQUIRED_FIELD.DESTINATION,
      label: 'Button Destination',
      message: 'Choose where the button goes.'
    });
  } else if (destination !== 'fancam') {
    // A campaign saved before Phase 1 narrowed the list can still carry a deferred destination.
    missing.push({
      key: REQUIRED_FIELD.DESTINATION,
      label: 'Button Destination',
      message: 'Phase 1 campaigns send the button to a FanCam challenge. Choose that destination.'
    });
  } else if (!destinationTarget) {
    missing.push({
      key: REQUIRED_FIELD.CHALLENGE,
      label: 'FanCam Challenge',
      message: 'Choose the challenge this campaign sends fans to.'
    });
  } else if (challengeResolved === null) {
    missing.push({
      key: REQUIRED_FIELD.CHALLENGE,
      label: 'FanCam Challenge',
      message: 'The chosen challenge no longer exists. Choose another.'
    });
  }

  if (audienceMode === AUDIENCE_MODE.CHANNEL_SPECIFIC) {
    if (!channelId) {
      missing.push({
        key: REQUIRED_FIELD.AUDIENCE,
        label: 'Channel',
        message: 'Choose the channel whose fans receive this campaign.'
      });
    }
  } else if (audienceMode === AUDIENCE_MODE.DIRECT_RECIPIENT) {
    if (!recipientId) {
      missing.push({
        key: REQUIRED_FIELD.AUDIENCE,
        label: 'Recipient',
        message: 'Choose the account this campaign goes to.'
      });
    }
  } else if (!filtersWereSet) {
    missing.push({
      key: REQUIRED_FIELD.AUDIENCE,
      label: 'Saved audience segment',
      message: 'Choose an audience segment, or set one on the Email Manager dashboard.'
    });
  }

  return missing;
}

/** Only the fields a draft cannot be saved without. */
export const missingToSaveDraft = (missing) =>
  missing.filter((field) => field.key === REQUIRED_FIELD.NAME);

/** "Campaign Name", "Campaign Name and Channel", "A, B and C". */
export function listFieldLabels(missing) {
  const labels = missing.map((field) => field.label);
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/** Moves the operator to a field: focuses its control, or brings its section into view. */
export function focusRequiredField(key) {
  const element = document.getElementById(requiredFieldId(key));
  if (!element) return;

  element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  if (typeof element.focus === 'function') element.focus({ preventScroll: true });
}
